import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import configService from '../services/configService';

function BehaviorSettings({ onComplete }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    rememberCallers: false,
    bossPhone: '',
    bossName: '',
    bossEmail: '',
    escalationTexts: false
  });
  
  const settingsRef = useRef(settings);
  
  // Keep ref in sync with state
  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  const saveBehaviorSettings = useCallback(async () => {
    if (!user?.attributes?.sub) return false;
    
    setLoading(true);
    try {
      // Get current settings from ref
      const currentSettings = settingsRef.current;
      
      // Format phone number for storage (+1 prefix)
      const phoneDigits = currentSettings.bossPhone.replace(/\D/g, '');
      const formattedPhone = phoneDigits ? `+1${phoneDigits}` : '';
      
      const behaviorData = {
        remember_past_callers: currentSettings.rememberCallers,
        manager_number: formattedPhone,
        manager_name: currentSettings.bossName.trim(),
        escalation_email: currentSettings.bossEmail.trim(),
        text_escalation_to_manager: currentSettings.escalationTexts
      };

      await configService.saveBehaviorSettings(behaviorData);
      console.log('✅ Behavior settings saved successfully');
      return true;
    } catch (error) {
      console.error('❌ Error saving behavior settings:', error);
      return false;
    } finally {
      setLoading(false);
    }
  }, [user?.attributes?.sub]);

  // Expose save function globally for Setup.js
  useEffect(() => {
    window.behaviorSettingsSave = saveBehaviorSettings;
    return () => {
      delete window.behaviorSettingsSave;
    };
  }, [saveBehaviorSettings]);

  useEffect(() => {
    loadBehaviorSettings();
  }, [user]);

  const loadBehaviorSettings = async () => {
    if (!user?.attributes?.sub) return;
    
    try {
      const config = await configService.getBusinessConfigByUserId(user.attributes.sub);
      if (config) {
        // Format phone number for display (remove +1 prefix and add dashes)
        const displayPhone = config.manager_number ? 
          config.manager_number.replace(/^\+1/, '').replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3') : '';
        
        setSettings({
          rememberCallers: config.remember_past_callers || false,
          bossPhone: displayPhone,
          bossName: config.manager_name || '',
          bossEmail: config.escalation_email || '',
          escalationTexts: config.text_escalation_to_manager || false
        });
      }
    } catch (error) {
      console.error('Error loading behavior settings:', error);
    }
  };

  // Auto-save for toggle fields only (not text inputs)
  const autoSaveToggles = useCallback(async (updatedSettings) => {
    if (!user?.attributes?.sub) return;
    
    try {
      // Format phone number for storage (+1 prefix)
      const phoneDigits = updatedSettings.bossPhone.replace(/\D/g, '');
      const formattedPhone = phoneDigits ? `+1${phoneDigits}` : '';
      
      const behaviorData = {
        remember_past_callers: updatedSettings.rememberCallers,
        manager_number: formattedPhone,
        manager_name: updatedSettings.bossName.trim(),
        escalation_email: updatedSettings.bossEmail.trim(),
        text_escalation_to_manager: updatedSettings.escalationTexts
      };

      await configService.saveBehaviorSettings(behaviorData);
      console.log('✅ Toggle settings auto-saved successfully');
    } catch (error) {
      console.error('❌ Error auto-saving toggle settings:', error);
    }
  }, [user?.attributes?.sub]);





  return (
    <div style={{ width: '100%', maxWidth: '500px', margin: '0 auto' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        
        {/* Remember Past Callers */}
        <div style={{ padding: '1.5rem', border: '1px solid #e5e7eb', borderRadius: '8px', background: 'white' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <h4 style={{ margin: 0, color: '#1e293b' }}>Remember Past Callers</h4>
            <label style={{ position: 'relative', display: 'inline-block', width: '50px', height: '24px' }}>
              <input
                type="checkbox"
                checked={settings.rememberCallers}
                onChange={(e) => {
                  const newSettings = { ...settings, rememberCallers: e.target.checked };
                  setSettings(newSettings);
                  // Auto-save toggle changes
                  autoSaveToggles(newSettings);
                }}
                style={{ opacity: 0, width: 0, height: 0 }}
              />
              <span style={{
                position: 'absolute',
                cursor: 'pointer',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: settings.rememberCallers ? '#2563eb' : '#ccc',
                transition: '0.4s',
                borderRadius: '24px'
              }}>
                <span style={{
                  position: 'absolute',
                  content: '',
                  height: '18px',
                  width: '18px',
                  left: settings.rememberCallers ? '29px' : '3px',
                  bottom: '3px',
                  backgroundColor: 'white',
                  transition: '0.4s',
                  borderRadius: '50%'
                }} />
              </span>
            </label>
          </div>
          <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem' }}>
            Greet returning customers by name when they call back.
          </p>
        </div>

        {/* Boss Information */}
        <div style={{ padding: '1.5rem', border: '1px solid #e5e7eb', borderRadius: '8px', background: 'white' }}>
          <h4 style={{ margin: '0 0 1rem 0', color: '#1e293b' }}>Know When the Boss is Calling</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#374151', fontWeight: '500' }}>
                Your Phone Number
              </label>
              <input
                type="tel"
                value={settings.bossPhone}
                onChange={(e) => {
                  const value = e.target.value;
                  // Remove any non-digit characters
                  const digits = value.replace(/\D/g, '');
                  
                  // Format as phone number and limit to 10 digits
                  let formatted = digits.slice(0, 10);
                  if (formatted.length >= 6) {
                    formatted = `${formatted.slice(0, 3)}-${formatted.slice(3, 6)}-${formatted.slice(6)}`;
                  } else if (formatted.length >= 3) {
                    formatted = `${formatted.slice(0, 3)}-${formatted.slice(3)}`;
                  }
                  
                  setSettings(prev => ({ ...prev, bossPhone: formatted }));
                }}
                placeholder="123-456-7890"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '1rem'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#374151', fontWeight: '500' }}>
                How should the assistant greet you?
              </label>
              <input
                type="text"
                value={settings.bossName}
                onChange={(e) => setSettings(prev => ({ ...prev, bossName: e.target.value }))}
                placeholder="e.g., Mr. Smith, Sarah, Boss"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '1rem'
                }}
              />
            </div>
          </div>
        </div>

        {/* Email Transcripts */}
        <div style={{ padding: '1.5rem', border: '1px solid #e5e7eb', borderRadius: '8px', background: 'white' }}>
          <h4 style={{ margin: '0 0 1rem 0', color: '#1e293b' }}>Email Call Transcripts</h4>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: '#374151', fontWeight: '500' }}>
              Your Email Address
            </label>
            <input
              type="email"
              value={settings.bossEmail}
              onChange={(e) => setSettings(prev => ({ ...prev, bossEmail: e.target.value }))}
              placeholder="your.email@company.com"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '1rem'
              }}
            />
            <p style={{ margin: '0.5rem 0 0 0', color: '#64748b', fontSize: '0.9rem' }}>
              Receive transcripts of all calls handled by your assistant.
            </p>
          </div>
        </div>

        {/* Escalation Texts */}
        <div style={{ padding: '1.5rem', border: '1px solid #e5e7eb', borderRadius: '8px', background: 'white' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <h4 style={{ margin: 0, color: '#1e293b' }}>Text for Escalations</h4>
            <label style={{ position: 'relative', display: 'inline-block', width: '50px', height: '24px' }}>
              <input
                type="checkbox"
                checked={settings.escalationTexts}
                onChange={(e) => {
                  const newSettings = { ...settings, escalationTexts: e.target.checked };
                  setSettings(newSettings);
                  // Auto-save toggle changes
                  autoSaveToggles(newSettings);
                }}
                style={{ opacity: 0, width: 0, height: 0 }}
              />
              <span style={{
                position: 'absolute',
                cursor: 'pointer',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: settings.escalationTexts ? '#2563eb' : '#ccc',
                transition: '0.4s',
                borderRadius: '24px'
              }}>
                <span style={{
                  position: 'absolute',
                  content: '',
                  height: '18px',
                  width: '18px',
                  left: settings.escalationTexts ? '29px' : '3px',
                  bottom: '3px',
                  backgroundColor: 'white',
                  transition: '0.4s',
                  borderRadius: '50%'
                }} />
              </span>
            </label>
          </div>
          <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem' }}>
            Get a text summary when customers need something the assistant can't handle. 
            Full details will be in the email transcript if enabled.
          </p>
        </div>

      </div>

      {loading && (
        <div style={{ textAlign: 'center', marginTop: '1rem', color: '#64748b' }}>
          Saving settings...
        </div>
      )}
    </div>
  );
}

export default BehaviorSettings;